/**
 * User Messages Migration Route - React Router v7
 *
 * Route: /user/messages/*
 * Description: User messages system (migrated version)
 */
import type {LoaderFunctionArgs} from 'react-router-dom'
import {useLoaderData} from 'react-router-dom'

import {withThirdPartiesParam} from '../../../hocs/withThirdPartiesParam.js'
import UserMessagesMigrationRaw from './index.js'

// Import styles
import './styles/index.scss'
// HOC wrapping that used to live in `./index.js`. Moved here so `./index.js`
// exports the raw component and receives Fast Refresh in dev.
const UserMessagesMigration = withThirdPartiesParam(UserMessagesMigrationRaw)

/**
 * Loader for User Messages Migration page
 * Fetches authentication token and tracking data
 */
export async function loader({context, request}: LoaderFunctionArgs) {
  const {domain} = context
  const url = new URL(request.url)
  const path = url.pathname

  try {
    // Step 1: Get authentication token and page name in parallel
    const [authTokenResult, pageNameResult] = await Promise.allSettled([
      domain.get('get_authentication_token_notifications_use_case').execute(),
      domain.get('get_page_name_page_use_case').execute({path})
    ])

    const authToken = authTokenResult.status === 'fulfilled' ? authTokenResult.value : null
    const pageName = pageNameResult.status === 'fulfilled' ? pageNameResult.value?.pageName : 'user_messages'

    // Step 2: Get page tracking data
    const [pageTrackingResult] = await Promise.allSettled([
      domain.get('get_page_tracking_data_use_case').execute({page: pageName})
    ])

    const pageTrackingData = pageTrackingResult.status === 'fulfilled' ? pageTrackingResult.value : {}

    return {
      authToken,
      pageName,
      pageTrackingData
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[UserMessagesMigration] Loader error:', error)
    return {
      authToken: null,
      pageName: 'user_messages',
      pageTrackingData: {}
    }
  }
}

/**
 * Component wrapper
 * Uses existing UserMessagesMigration component
 */
export function Component() {
  const data = useLoaderData()
  return <UserMessagesMigration {...data} />
}

export default {loader, Component}
